'use client';

import React, { ReactNode, createContext, useContext, useState, useCallback } from 'react';
import { IAssessmentParam, ProviderProps } from '../types/types';
import { requestHandler } from '../utils/requestHandler';
import { useToast } from './toast-provider';
import { createAssessmentParamRequest, deleteAssessmentParamRequest, getAllAssessmentParamsRequest, getOneAssessmentParamRequest, updateAssessmentParamRequest } from '../utils/handlers/client/assessment';
export const dynamic = 'force-dynamic';
// Helper function to build cache key from query
const buildCacheKey = query => {
  const normalizedEntries = Object.entries(query || {}).filter(([, value]) => value !== undefined && value !== null && value !== '').sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  return JSON.stringify(normalizedEntries);
};

// Create a context to manage AssessmentParams-related data and functions
const AssessmentParamContext = createContext({
  error: '',
  map: {},
  list: [],
  setList: list => {},
  setMap: map => {},
  metaData: {},
  createAssessmentParam: async (data, setIsLoading, isNotify = true) => {
    return {};
  },
  getAllAssessmentParams: async (query, setIsLoading, options) => {},
  updateAssessmentParam: async (id, data, setIsLoading, isNotify = true) => {
    return {};
  },
  deleteAssessmentParam: async (id, data, setIsLoading, isNotify = true) => {
    return {};
  },
  getOneAssessmentParam: async (id, data, setIsLoading) => {
    return {};
  },
  clearCache: () => {}
});

// Create a hook to access the AssessmentParamContext
const useAssessmentParamContext = () => useContext(AssessmentParamContext);

// Create a component
const AssessmentParamProvider = ({
  children
}) => {
  const {
    showErrorToast,
    showSuccessToast,
    showWarningToast
  } = useToast();
  const [map, setMap] = useState({});
  const [list, setList] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [error, setError] = useState('');
  const [pageCache, setPageCache] = useState({});

  // Clear cache function
  const clearCache = useCallback(() => {
    setPageCache({});
  }, []);
  // Function to handle create AssessmentParam
  const createAssessmentParam = async (data, setIsLoading, isNotify = true) => {
    setError('');
    return await requestHandler(async () => await createAssessmentParamRequest(data), res => {
      const {
        data,
        message
      } = res;
      const AssessmentParamId = data.id;
      setMap(prevMap => ({
        ...prevMap,
        [AssessmentParamId]: data
      }));
      // check list contains AssessmentParamId
      if (!list.includes(AssessmentParamId)) {
        setList(prevList => [AssessmentParamId, ...prevList]);
      }
      // Clear cache since new param affects all queries
      setPageCache({});
      isNotify && showSuccessToast(message);
    }, errMessage => {
      isNotify && showErrorToast(errMessage);
      setError(errMessage);
      throw new Error(errMessage);
    }, setIsLoading);
  };
  const getOneAssessmentParam = async (id, data, setIsLoading) => {
    setError('');
    return await requestHandler(async () => await getOneAssessmentParamRequest(id, data), res => {
      const {
        data
      } = res;
      setMap(prevMap => ({
        ...prevMap,
        [id]: data
      }));
    }, errMessage => {
      showErrorToast(errMessage);
      setError(errMessage);
    }, setIsLoading);
  };
  // Function to handle get all AssessmentParams with caching
  const getAllAssessmentParams = useCallback(async (query, setIsLoading, options) => {
    const cacheKey = buildCacheKey(query);
    const cachedEntry = pageCache[cacheKey];

    // Return cached data if available (unless skipCache is true)
    if (cachedEntry && !options?.skipCache) {
      setMap(prevMap => ({
        ...prevMap,
        ...cachedEntry.map
      }));
      setList(cachedEntry.ids);
      setMetaData(prevMeta => ({
        ...prevMeta,
        ...cachedEntry.meta
      }));
      return;
    }
    await requestHandler(async () => await getAllAssessmentParamsRequest(query), res => {
      const {
        data
      } = res;
      const {
        AssessmentParams = [],
        ...metaData
      } = data;
      const AssessmentParamMap = AssessmentParams.reduce((map, AssessmentParam) => {
        map[AssessmentParam?.id] = AssessmentParam;
        return map;
      }, {});

      // Preserve API order (createdAt desc) for list display
      const AssessmentParamIds = AssessmentParams.map(p => String(p?.id)).filter(Boolean);

      // Update cache
      setPageCache(prevCache => ({
        ...prevCache,
        [cacheKey]: {
          ids: AssessmentParamIds,
          map: AssessmentParamMap,
          meta: metaData
        }
      }));
      setMap(prevMap => ({
        ...prevMap,
        ...AssessmentParamMap
      }));
      setList(AssessmentParamIds);
      setMetaData(metaData);
    }, errorMsg => {
      console.log(errorMsg);
      showErrorToast(errorMsg);
    }, setIsLoading);
  }, [pageCache, showErrorToast]);
  const updateAssessmentParam = async (id, data, setIsLoading, isNotify = true) => {
    setError('');
    return await requestHandler(async () => await updateAssessmentParamRequest(id, data), res => {
      const {
        data,
        message
      } = res;
      const AssessmentParamId = data.id;
      setMap(prevMap => ({
        ...prevMap,
        [AssessmentParamId]: data
      }));
      // Clear cache since param update might affect filtering
      setPageCache({});
      isNotify && showSuccessToast(message);
    }, errMessage => {
      isNotify && showErrorToast(errMessage);
      setError(errMessage);
      throw new Error(errMessage);
    }, setIsLoading);
  };

  // Function to handle delete AssessmentParam
  const deleteAssessmentParam = async (id, data, setIsLoading, isNotify = true) => {
    setError('');
    return await requestHandler(async () => await deleteAssessmentParamRequest(id, data), res => {
      const {
        message
      } = res;
      setMap(prevMap => Object.fromEntries(Object.entries(prevMap).filter(([key]) => key !== id.toString())));
      setList(prevList => prevList.filter(item => item !== id.toString()));
      // Clear cache since deletion affects all queries
      setPageCache({});
      isNotify && showSuccessToast(message);
    }, errMessage => {
      isNotify && showErrorToast(errMessage);
      setError(errMessage);
    }, setIsLoading);
  };

  // Provide AssessmentParams-related data and functions through the context
  return <AssessmentParamContext.Provider value={{
    error,
    map,
    list,
    setList,
    setMap,
    metaData,
    createAssessmentParam,
    getAllAssessmentParams,
    updateAssessmentParam,
    deleteAssessmentParam,
    getOneAssessmentParam,
    clearCache
  }}>
            {children}
        </AssessmentParamContext.Provider>;
};

// Export the context, provider component, and custom hook
export { AssessmentParamContext, AssessmentParamProvider, useAssessmentParamContext };